import React, { useState, useEffect, useCallback } from 'react';
import { Button, Col, Row, ModalBody, Form, Spinner, Card, CardBody, CardFooter, Label, Input, FormFeedback } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import { setRequireCloseModal } from '../../../../../slices/screen/reducer';
import { addNewPage, updatePage } from '../../../../../slices/thunks';
import { getPageTypeList } from '../../../../../helpers/screen/backend_helper';
import OSelect from '../../../../../Components/OElements/OSelect';

import CoverPageImage from "../../../../../assets/images/report/thumbnail/CoverPage.png";
import ImageSectionPage from "../../../../../assets/images/report/thumbnail/ImageSectionPage.png";
import TitleGraphTextPageImage from "../../../../../assets/images/report/thumbnail/TitleGraphTextPage.png";
import TitleTextGraphPageImage from "../../../../../assets/images/report/thumbnail/TitleTextGraphPage.png";
import TitleGraphPageImage from "../../../../../assets/images/report/thumbnail/TitleGraphPage.png";
import TitleGraphGraphPageImage from "../../../../../assets/images/report/thumbnail/TitleGraphGraphPage.png";

import OHr from '../../../../../Components/OElements/OHr';
import PageContentRenderer from '../../Components/PageContentRenderer';

const imageMap = {
    'CoverPage.png': CoverPageImage,
    'ImageSectionPage.png': ImageSectionPage,
    'TitleGraphTextPage.png': TitleGraphTextPageImage,
    'TitleTextGraphPage.png': TitleTextGraphPageImage,
    'TitleGraphPage.png': TitleGraphPageImage,
    'TitleGraphGraphPage.png': TitleGraphGraphPageImage,
};

const PageForm = ({ dataPage, isEdit, handleToggleModal, isOpen }) => {
    const dispatch = useDispatch();

    const selectorPage = createSelector(
        state => state.Screen,
        data => ({
            isAdding: data.isAdding,
            isAdded: data.isAdded,
            isUpdating: data.isUpdating,
            isUpdated: data.isUpdated,
            isLoadingList: data.isLoadingList,
            requireCloseModal: data.requireCloseModal
        })
    );
    const { isAdding, isUpdating, requireCloseModal } = useSelector(selectorPage);

    const [selectedPageType, setSelectedPageType] = useState(null);
    const [dynamicElements, setDynamicElements] = useState(dataPage?.elements || []);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: dataPage?.name || '',
            page_type_id: dataPage?.page_type_id || '',
            description: dataPage?.description || '',
            elements: dataPage?.elements || [],
            graph: dataPage?.graph || '',
            graph1: dataPage?.graph1 || '',
            graph2: dataPage?.graph2 || '',
        },
        validationSchema: Yup.object({
            name: Yup.string().required('El nombre es obligatorio'),
            page_type_id: Yup.string().required('El tipo de página es obligatorio'),
            description: Yup.string(),
            graph: Yup.string().nullable(),
            graph1: Yup.string().nullable(),
            graph2: Yup.string().nullable(),
        }),
        validateOnChange: true,
        onSubmit: async (values, { setSubmitting, setErrors }) => {
            try {
                const PageData = {
                    name: values.name,
                    page_type_id: values.page_type_id,
                    description: values.description,
                    elements: Array.isArray(dynamicElements) ? dynamicElements : [dynamicElements],
                    ...(isEdit && { _id: dataPage._id }),
                };

                if (isEdit) {
                    await dispatch(updatePage(PageData));
                } else {
                    await dispatch(addNewPage(PageData));
                }
            } catch (error) {
                // Manejo de errores en la respuesta de la API
                setErrors({ submit: 'Ocurrió un error al guardar la página.' });
            } finally {
                setSubmitting(false);
                dispatch(setRequireCloseModal(true));
            }
        },
    });

    // Optimizar los efectos para evitar renders innecesarios
    useEffect(() => {
        if (isEdit && dataPage?.elements) {
            setDynamicElements(Array.isArray(dataPage.elements) ? dataPage.elements : []);
            setSelectedPageType({ value: dataPage.page_type_id, ...dataPage });
        }
    }, [isEdit, dataPage]);

    const handlePageTypeSelected = useCallback((optionSelected) => {
        setSelectedPageType(optionSelected);
        setDynamicElements([]); // Reiniciar elementos dinámicos cuando cambia el tipo de página
    }, []);

    const handleDynamicChange = useCallback((index, field, value) => {
        setDynamicElements(prevElements => {
            const newElements = [...prevElements];
            newElements[index] = { ...newElements[index], [field]: value };
            return newElements;
        });
    }, []);

    useEffect(() => {
        if (isOpen && requireCloseModal) {
            dispatch(setRequireCloseModal(false));
            handleToggleModal();
        }
    }, [isOpen, requireCloseModal, dispatch, handleToggleModal]);

    return (
        <Form
            className='tablelist-form'
            onSubmit={e => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
            }}
        >
            <ModalBody className='modal-body'>
                <Row className='g-3'>
                    <Col lg={3}>
                        <OSelect
                            key={`page_type_id`}
                            label='Tipo de Pantalla'
                            id='page_type_id'
                            name='page_type_id'
                            value={validation.values.page_type_id}
                            onSelect={handlePageTypeSelected}
                            requestApi={() => getPageTypeList()}
                            validation={validation}
                        />

                        {selectedPageType && (
                            <Card className='mt-2'>
                                <CardBody className='text-center'>
                                    <img
                                        src={imageMap[selectedPageType.thumbnail_image]}
                                        className="me-3"
                                        alt={selectedPageType.name}
                                        style={{ width: '80%' }}
                                    />
                                </CardBody>
                                <CardFooter>
                                    {selectedPageType.description}
                                </CardFooter>
                            </Card>
                        )}
                    </Col>
                    <Col lg={9}>
                        <Row>
                            <Col>
                                <Label for='name' className='form-label'>
                                    Nombre de la página
                                </Label>
                                <Input
                                    name='name'
                                    id='name'
                                    className='form-control'
                                    placeholder='Ingrese un nombre'
                                    type='text'
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.name || ''}
                                    invalid={validation.touched.name && validation.errors.name ? true : false}
                                />
                                {validation.touched.name && validation.errors.name && (
                                    <FormFeedback type='invalid'>
                                        {validation.errors.name}
                                    </FormFeedback>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Label for='description' className='form-label'>
                                    Descripción
                                </Label>
                                <Input
                                    name='description'
                                    id='description'
                                    className='form-control'
                                    placeholder='Ingrese una descripción'
                                    type='text'
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.description || ''}
                                    invalid={validation.touched.description && validation.errors.description ? true : false}
                                />
                                {validation.touched.description && validation.errors.description && (
                                    <FormFeedback type='invalid'>
                                        {validation.errors.description}
                                    </FormFeedback>
                                )}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <OHr text={'Estructura de la página'} />
                                {selectedPageType && (
                                    <PageContentRenderer
                                        pageType={selectedPageType.code}
                                        elements={dynamicElements}
                                        handleDynamicChange={handleDynamicChange}
                                        validation={validation}
                                    />
                                )}
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </ModalBody>
            <div className='modal-footer'>
                <div className='hstack gap-2 justify-content-end'>
                    <Button
                        type='button'
                        onClick={handleToggleModal}
                        className='btn-danger btn-label'>
                        <i className="icon-x label-icon"></i>
                        Cancelar
                    </Button>
                    <button
                        type='submit'
                        className='btn btn-label btn-primary'
                        id='add-btn'
                        disabled={isAdding || isUpdating || validation.isSubmitting}
                    >
                        {isAdding || isUpdating || validation.isSubmitting ? (
                            <i className='label-icon'><Spinner size='sm' /></i>
                        ) : <i className="ri-save-3-line label-icon"></i>}
                        {isEdit ? 'Editar' : 'Crear'} Página
                    </button>
                </div>
            </div>
        </Form>
    );
};

export default PageForm;
